import { Module } from "vuex";
import ProductService from "@/services/product.service";
import { IncludedProduct, TariffArray } from "@/struct/TariffStruct";
import type { RootState } from "@/store";

export interface ProductState {
  isLoading: boolean;
  availableAddons: TariffArray[];
  isAddonsLoading: boolean;
}

const productsService = new ProductService();

const productModule: Module<ProductState, RootState> = {
  namespaced: true,
  state: () => ({
    isLoading: false,
    availableAddons: [],
    isAddonsLoading: false,
  }),
  getters: {},
  mutations: {
    SET_LOADING(state, value: boolean) {
      state.isLoading = value;
    },
    SET_AVAILABLE_ADDONS(state, payload) {
      state.availableAddons = payload;
    },
    SET_ADDONS_LOADING(state, payload) {
      state.isAddonsLoading = payload;
    },
  },
  actions: {
    addProduct({ commit }, productId: number) {
      commit("SET_LOADING", true);
      return productsService.addProduct(productId).finally(() => {
        commit("SET_LOADING", false);
      });
    },
    disableAddon({ dispatch }, productId: number) {
      return productsService
        .removeProduct(productId)
        .then((res) => {
          if (!res.data.response) return false;
          return dispatch("tariff/updateSubscriptionDetails", null, {
            root: true,
          }).then(() => true);
        })
        .catch(() => false);
    },
    fetchAvailableAddons({ commit, rootState, rootGetters }) {
      const mainTariff = rootGetters["tariff/mainTariff"];
      if (!mainTariff) return;

      commit("SET_ADDONS_LOADING", true);
      let includedAddons: IncludedProduct[] = [];

      return productsService
        .getProducts(
          mainTariff.subscriptionId,
          mainTariff.compatibleId,
          mainTariff.facility
        )
        .then((res) => {
          includedAddons = res.data.response;
        })
        .catch((err) => {
          console.error(err);
        })
        .then(() => productsService.getAddons())
        .then((res) => {
          if (!res.data?.data) return;

          const allAddons = res.data.data.filter((a: TariffArray) => a.publish);
          const enabledAddons =
            rootState.tariff.subscriptionDetails.additionalProducts;
          const enabledIds = new Set(enabledAddons.map((p) => p.productId));
          const filteredAddons = allAddons.filter(
            (addon: TariffArray) => !enabledIds.has(addon.ems_product_id)
          );

          if (includedAddons.length) {
            const includedIds = new Set(includedAddons.map((p) => p.id));
            commit(
              "SET_AVAILABLE_ADDONS",
              filteredAddons.filter(
                (addon: TariffArray) =>
                  includedIds.has(addon.ems_product_id) &&
                  addon.available_to_activate === 1
              )
            );
          } else {
            commit("SET_AVAILABLE_ADDONS", filteredAddons);
          }
        })
        .catch((err) => {
          console.error("Failed to get addons:", err);
        })
        .finally(() => {
          commit("SET_ADDONS_LOADING", false);
        });
    },
  },
};

export default productModule;
